/**
 * Per E-Mail auf die Antwort eines Gastes antworten, direkt aus der Anfragen-Ansicht
 * (Antoine, 19.10.2026: der Betreiber übersah Gast-Antworten auf ein Angebot; die Anfrage ging
 * verloren). Der Drawer zeigt die letzte Gast-Nachricht (gekürzt, voll aufklappbar), dazu Betreff
 * und Text der Antwort.
 *
 * Versand: `POST /api/events/:id/reply` (dieselbe Route wie die Gespräch-Karte im Vorgang).
 * Danach `POST /api/inbox/mark-read` — die Antwort ist beantwortet, das Zeichen auf der Karte
 * verschwindet. Ein Fehlschlag beim Gelesen-Markieren meldet die Antwort NICHT als fehlgeschlagen:
 * die Mail ist raus.
 */
import { useEffect, useMemo, useState } from "react";
import { apiMutate, useApi } from "../hooks/useApi";
import type { ApiError } from "../lib/api";
import { Button, Drawer, Input, Textarea } from "./components";
import { formatDateTime } from "./format";
import type { Inquiry } from "./types";

/** Der Teil einer Mail-Zeile (`GET /api/events/:id/emails`), den der Drawer braucht. */
interface GastMailZeile {
  id: string;
  direction: "inbound" | "outbound";
  subject: string | null;
  bodyText: string | null;
  receivedAt: string;
  isAutoReply?: boolean | null;
  readAt?: string | null;
}

const KURZ_ZEICHEN = 600;

/** Fehlertext zu einem Versandfehler — dieselben Wörter wie in der Gespräch-Karte. */
export function antwortFehlerText(err: unknown): string {
  const apiErr = err as Partial<ApiError> | null;
  if (apiErr?.code === "no_recipient") {
    return "Der Kunde hat keine E-Mail-Adresse hinterlegt — die Antwort wurde nicht versendet.";
  }
  if (apiErr?.code === "mail_not_configured") return "Mail-Versand ist für diesen Mandanten nicht eingerichtet.";
  if (apiErr?.code === "send_failed") return `Der Versand ist fehlgeschlagen: ${apiErr.message ?? "unbekannter Fehler"}`;
  return err instanceof Error ? err.message : String(err);
}

function vorgabeBetreff(betreff: string | null | undefined): string {
  const b = betreff?.trim();
  if (!b) return "Antwort";
  return /^(re|aw):/i.test(b) ? b : `Re: ${b}`;
}

interface Props {
  /** Die Anfrage, auf die geantwortet wird; `null` = Drawer zu. */
  anfrage: Inquiry | null;
  onClose: () => void;
  /** Nach erfolgreichem Senden (Liste neu laden). */
  onGesendet: () => void;
}

export function AnfrageAntwortDrawer({ anfrage, onClose, onGesendet }: Props) {
  const eventId = anfrage?.id ?? null;
  const { data, loading, error } = useApi<GastMailZeile[]>(eventId ? `/api/events/${eventId}/emails` : null);
  const [betreff, setBetreff] = useState("");
  const [text, setText] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [vollText, setVollText] = useState(false);

  /** Die jüngste Gast-Nachricht (keine Abwesenheitsnotiz). */
  const letzte = useMemo(() => {
    const gast = (data ?? []).filter((m) => m.direction === "inbound" && !m.isAutoReply);
    return gast.sort((a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime())[0] ?? null;
  }, [data]);

  // Beim Öffnen für eine andere Anfrage frisch anfangen.
  useEffect(() => {
    setBetreff("");
    setText("");
    setFehler(null);
    setVollText(false);
  }, [eventId]);
  // Betreff-Vorgabe, sobald die Gast-Mail da ist — aber nie über eine Eingabe hinweg.
  useEffect(() => {
    if (!eventId || loading) return;
    setBetreff((alt) => (alt ? alt : vorgabeBetreff(letzte?.subject)));
  }, [eventId, loading, letzte]);

  async function senden() {
    if (!eventId || !text.trim()) return;
    setLaeuft(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}/reply`, "POST", {
        channel: "email",
        subject: betreff.trim() || vorgabeBetreff(letzte?.subject),
        text: text.trim(),
      });
    } catch (err) {
      setFehler(antwortFehlerText(err));
      setLaeuft(false);
      return;
    }
    // Die Mail ist raus. Gelesen-Markieren darf scheitern, ohne den Versand zu entwerten.
    try {
      await apiMutate("/api/inbox/mark-read", "POST", { eventId });
    } catch {
      /* Zeichen bleibt, bis der Vorgang geöffnet wird */
    }
    setLaeuft(false);
    onGesendet();
    onClose();
  }

  const inhalt = letzte?.bodyText?.trim() ?? "";
  const gekuerzt = !vollText && inhalt.length > KURZ_ZEICHEN ? `${inhalt.slice(0, KURZ_ZEICHEN).trimEnd()} …` : inhalt;

  return (
    <Drawer
      open={anfrage != null}
      onClose={onClose}
      title="Per E-Mail antworten"
      subtitle={anfrage?.customerName}
      footer={
        <>
          <Button type="button" variant="secondary" disabled={laeuft} onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="button" data-testid="antwort-senden" disabled={laeuft || !text.trim()} onClick={() => void senden()}>
            {laeuft ? "Sendet …" : "Antwort senden"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <section
          data-testid="antwort-gast-mail"
          className="rounded-xl border border-outline-variant/40 bg-surface-container-high/40 p-4 dark:border-outline/30 dark:bg-surface-variant/20"
        >
          {loading ? (
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Lädt …</p>
          ) : error ? (
            <p className="font-body-sm text-body-sm text-error">{error}</p>
          ) : !letzte ? (
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Keine Nachricht des Gastes gefunden.</p>
          ) : (
            <>
              <p className="font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                Letzte Nachricht des Gastes · {formatDateTime(letzte.receivedAt)}
              </p>
              <p className="mt-1 font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                {letzte.subject?.trim() || "(ohne Betreff)"}
              </p>
              <p className="mt-2 whitespace-pre-wrap break-words font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                {gekuerzt || "(kein Text)"}
              </p>
              {inhalt.length > KURZ_ZEICHEN && (
                <button
                  type="button"
                  onClick={() => setVollText((v) => !v)}
                  className="mt-2 text-[12px] font-medium text-primary hover:underline"
                >
                  {vollText ? "Weniger anzeigen" : "Ganze Nachricht anzeigen"}
                </button>
              )}
            </>
          )}
        </section>

        <label className="block font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
          Betreff
          <Input className="mt-1" value={betreff} maxLength={300} onChange={(e) => setBetreff(e.target.value)} />
        </label>
        <label className="block font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
          Deine Antwort
          <Textarea
            className="mt-1"
            rows={8}
            maxLength={20000}
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        {fehler && (
          <p data-testid="antwort-fehler" role="alert" className="font-body-sm text-[12.5px] text-error">
            {fehler}
          </p>
        )}
      </div>
    </Drawer>
  );
}
